import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, X, Check, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAY_OPTS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const TasksTab = ({ api, kid, onChanged }) => {
  const [tasks, setTasks] = useState([]);
  const [todayLogs, setTodayLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', points: 10, recurrence: 'daily', days: [], emoji: '⭐' });
  const [suggestions, setSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [t, l] = await Promise.all([api.get(`/kids/${kid.kid_id}/tasks`), api.get(`/kids/${kid.kid_id}/tasks/today`)]);
      setTasks(t.data); setTodayLogs(l.data);
    } catch (e) { console.error(e); }
  }, [api, kid.kid_id]);

  useEffect(() => { fetchData(); setSuggestions([]); }, [fetchData]);

  const saveTask = async (payload) => {
    try {
      await api.post(`/kids/${kid.kid_id}/tasks`, payload);
      toast.success('Task added');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await saveTask({ ...form, points: parseInt(form.points) });
    setShowForm(false);
    setForm({ title: '', points: 10, recurrence: 'daily', days: [], emoji: '⭐' });
  };

  const handleDelete = async (id) => { await api.delete(`/kids/tasks/${id}`); fetchData(); };

  const approveLog = async (logId) => {
    try {
      const r = await api.post(`/kids/task-log/${logId}/approve`);
      toast.success(`Approved! +${r.data.points} points`);
      if (r.data.streak_bonus) toast.success(`🔥 ${r.data.streak_bonus.streak}-day streak! Bonus +${r.data.streak_bonus.bonus} points!`);
      fetchData(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const getAISuggestions = async () => {
    setLoadingAI(true);
    try {
      const r = await api.post('/kids/ai-suggest', { kid_id: kid.kid_id, kind: 'tasks' });
      setSuggestions(r.data.suggestions || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'AI suggestion failed'); }
    setLoadingAI(false);
  };

  const statusBadge = (s) => ({
    pending: 'bg-muted text-muted-foreground', done: 'bg-blue-500/10 text-blue-600',
    approved: 'bg-green-500/10 text-green-600', rejected: 'bg-red-500/10 text-red-500',
    missed: 'bg-orange-500/10 text-orange-500',
  }[s] || 'bg-muted');

  return (
    <div className="space-y-5" data-testid="kids-tasks-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">{kid.name}'s chores & activities — each completed task earns ⭐ points after your approval</p>
        <div className="flex gap-2">
          <button onClick={getAISuggestions} disabled={loadingAI} data-testid="ai-suggest-tasks-btn"
            className="px-3 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
            <Sparkle size={14} /> {loadingAI ? 'Thinking…' : 'AI Suggest'}
          </button>
          <button onClick={() => setShowForm(true)} data-testid="add-task-btn"
            className="px-3 py-2 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> Add Task</button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="glass-card p-4 border-purple-500/30" data-testid="ai-suggestions-box">
          <p className="text-xs font-semibold text-purple-600 mb-2 flex items-center gap-1"><Sparkle size={12} /> AI suggestions for {kid.name} — tap to add</p>
          <div className="flex gap-2 flex-wrap">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => { saveTask({ title: s.title, points: s.points || 10, recurrence: s.recurrence || 'daily', emoji: s.emoji || '⭐' }); setSuggestions(prev => prev.filter((_, j) => j !== i)); }}
                className="px-3 py-1.5 rounded-full border border-purple-500/40 bg-purple-500/5 text-sm hover:bg-purple-500/15">
                {s.emoji} {s.title} <span className="text-amber-600 font-semibold">+{s.points}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card p-5">
          <h3 className="font-semibold text-sm mb-3">Today's Checklist</h3>
          <div className="space-y-2">
            {todayLogs.map(l => (
              <div key={l.log_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40" data-testid={`task-log-${l.log_id}`}>
                <span className="text-lg">{l.emoji || '⭐'}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{l.title}</p>
                  <p className="text-xs text-amber-600 font-semibold">+{l.points} pts</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusBadge(l.status)}`}>{l.status}</span>
                {(l.status === 'pending' || l.status === 'done') && (
                  <button onClick={() => approveLog(l.log_id)} data-testid={`approve-log-${l.log_id}`}
                    className="p-1.5 rounded-full bg-green-600 text-white" title="Approve & credit points"><Check size={13} weight="bold" /></button>
                )}
              </div>
            ))}
            {todayLogs.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No tasks due today</p>}
          </div>
        </div>

        <div className="glass-card p-5">
          <h3 className="font-semibold text-sm mb-3">All Task Definitions</h3>
          <div className="space-y-2">
            {tasks.map(t => (
              <div key={t.task_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border" data-testid={`task-def-${t.task_id}`}>
                <span className="text-lg">{t.emoji || '⭐'}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{t.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{t.recurrence}{t.recurrence === 'weekly' && t.days?.length > 0 && `: ${t.days.join(', ')}`} · +{t.points} pts</p>
                </div>
                <button onClick={() => handleDelete(t.task_id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
            {tasks.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No tasks yet — add one or use AI Suggest</p>}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-card rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="task-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Add Task</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <input data-testid="task-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                placeholder="e.g. Finish homework" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Points</label>
                  <input data-testid="task-points-input" type="number" min="1" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Repeats</label>
                  <select data-testid="task-recurrence-select" value={form.recurrence} onChange={e => setForm({ ...form, recurrence: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="once">One time (today)</option>
                  </select>
                </div>
              </div>
              {form.recurrence === 'weekly' && (
                <div className="flex gap-1 flex-wrap">
                  {DAY_OPTS.map(d => (
                    <button type="button" key={d} onClick={() => setForm({ ...form, days: form.days.includes(d) ? form.days.filter(x => x !== d) : [...form.days, d] })}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${form.days.includes(d) ? 'bg-[#FF6B35] text-white border-transparent' : 'border-border'}`}>{d}</button>
                  ))}
                </div>
              )}
              <button type="submit" data-testid="task-save-btn" className="w-full px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Add Task</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
